import React from 'react';
import { View, StyleSheet, Share } from 'react-native';
import { IconButton, useTheme } from 'react-native-paper';
import { Heart, Share2, Bookmark } from 'lucide-react-native';
import { logger } from '../../utils/logger';

interface HeaderActionsProps {
  /** Whether the heart icon should show as liked/favorited */
  isLiked?: boolean;
  /** Callback when heart/like button is pressed */
  onLikePress?: () => void;
  /** Whether the like button is in loading state */
  isLiking?: boolean;
  /** Whether the bookmark icon should show as bookmarked */
  isBookmarked?: boolean;
  /** Callback when bookmark button is pressed */
  onBookmarkPress?: () => void;
  /** Whether the bookmark button is in loading state */
  isBookmarking?: boolean;
  /** Share content configuration */
  shareContent?: {
    title: string;
    message: string;
    url?: string;
  };
  /** Custom share handler (overrides default share functionality) */
  onSharePress?: () => void;
  /** Additional test IDs for testing */
  testID?: string;
}

export function HeaderActions({
  isLiked = false,
  onLikePress,
  isLiking = false,
  isBookmarked = false,
  onBookmarkPress,
  isBookmarking = false,
  shareContent,
  onSharePress,
  testID,
}: HeaderActionsProps) {
  const theme = useTheme();

  const handleShare = async () => {
    if (onSharePress) {
      onSharePress();
      return;
    }

    if (!shareContent) return;

    try {
      const result = await Share.share({
        message: shareContent.message,
        title: shareContent.title,
        url: shareContent.url,
      });

      if (result.action === Share.sharedAction) {
        if (result.activityType) {
          // shared with activity type of result.activityType
        } else {
          // shared
        }
      } else if (result.action === Share.dismissedAction) {
        // dismissed
      }
    } catch (error) {
      logger.error('Error sharing content:', error);
    }
  };

  return (
    <View style={styles.container} testID={testID}>
      {onBookmarkPress && (
        <IconButton
          icon={() => (
            <Bookmark
              size={16}
              color={
                isBookmarked ? theme.colors.primary : theme.colors.onSurface
              }
              fill={isBookmarked ? theme.colors.primary : 'none'}
            />
          )}
          size={16}
          onPress={onBookmarkPress}
          disabled={isBookmarking}
          style={styles.iconButton}
          testID={`${testID}-bookmark`}
        />
      )}
      <IconButton
        icon={() => <Share2 size={16} color={theme.colors.onSurface} />}
        size={16}
        onPress={handleShare}
        style={styles.iconButton}
        testID={`${testID}-share`}
      />
      {onLikePress && (
        <IconButton
          icon={() => (
            <Heart
              size={16}
              color={isLiked ? '#ff4757' : theme.colors.onSurface}
              fill={isLiked ? '#ff4757' : 'none'}
            />
          )}
          size={16}
          onPress={onLikePress}
          disabled={isLiking}
          style={styles.iconButton}
          testID={`${testID}-like`}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
  },
  iconButton: {
    margin: 0,
    marginLeft: 4,
  },
});
